import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Modal, PrimaryButton, SecondaryButton } from '../../components';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { subscribeToMailchimp } from '../../util/api';

import css from './MailchimpSubscriberPopup.module.css';

export const MAILCHIMP_POPUP_DISMISSED_AT_KEY = 'mailchimpPopupDismissedAt';
export const MAILCHIMP_POPUP_SUBSCRIBED_KEY = 'mailchimpPopupSubscribed';

export const excludedMailchimpPopupRoutes = [
  '/checkout',
  '/payment',
  '/booking',
  '/order',
  '/orders',
  '/sale',
  '/sales',
  '/transactions',
  '/login',
  '/signup',
  '/confirm',
  '/recover-password',
  '/reset-password',
  '/verify-email',
  '/account/payments',
  '/account/payment-methods',
];

const DEFAULT_DELAY_MS = 7000;
const DEFAULT_DISMISS_DAYS = 7;
const SUCCESS_CLOSE_DELAY_MS = 1800;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';
const normalizeString = value => (typeof value === 'string' ? value.trim() : '');

const parseNonNegativeInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const storageGet = key => {
  if (!isBrowser()) {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
};

const storageSet = (key, value) => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch (_e) {
    // If storage is blocked, the popup still works for the current page view.
  }
};

export const isMailchimpPopupRouteExcluded = pathname => {
  const normalizedPath = normalizeString(pathname).toLowerCase() || '/';

  return excludedMailchimpPopupRoutes.some(route => {
    return (
      normalizedPath === route ||
      normalizedPath.startsWith(`${route}/`) ||
      normalizedPath.startsWith(`${route}-`) ||
      normalizedPath.includes(`${route}/`) ||
      normalizedPath.includes(`${route}-`) ||
      normalizedPath.endsWith(route)
    );
  });
};

const hasRecentDismissal = dismissDays => {
  const dismissedAt = Number.parseInt(storageGet(MAILCHIMP_POPUP_DISMISSED_AT_KEY), 10);
  if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) {
    return false;
  }

  const dismissMs = dismissDays * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt < dismissMs;
};

const shouldSuppressPopup = ({ pathname, dismissDays }) => {
  if (!isBrowser()) {
    return true;
  }

  if (storageGet(MAILCHIMP_POPUP_SUBSCRIBED_KEY) === 'true') {
    return true;
  }

  if (hasRecentDismissal(dismissDays)) {
    return true;
  }

  return isMailchimpPopupRouteExcluded(pathname);
};

const getCurrentUserInitialValues = currentUser => {
  const profile = currentUser?.attributes?.profile || {};

  return {
    email: normalizeString(currentUser?.attributes?.email),
    firstName: normalizeString(profile.firstName),
    lastName: normalizeString(profile.lastName),
  };
};

const MailchimpSubscriberPopup = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUser = useSelector(state => state?.user?.currentUser);
  const userInitialValues = useMemo(() => getCurrentUserInitialValues(currentUser), [currentUser]);

  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState({
    email: userInitialValues.email,
    firstName: userInitialValues.firstName,
    lastName: userInitialValues.lastName,
  });
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const previousFocusRef = useRef(null);
  const successCloseTimerRef = useRef(null);

  const popupEnabled = process.env.REACT_APP_MAILCHIMP_POPUP_ENABLED !== 'false';
  const delayMs = parseNonNegativeInteger(
    process.env.REACT_APP_MAILCHIMP_POPUP_DELAY_MS,
    DEFAULT_DELAY_MS
  );
  const dismissDays = parseNonNegativeInteger(
    process.env.REACT_APP_MAILCHIMP_POPUP_DISMISS_DAYS,
    DEFAULT_DISMISS_DAYS
  );

  useEffect(() => {
    setValues(previousValues => ({
      email: previousValues.email || userInitialValues.email,
      firstName: previousValues.firstName || userInitialValues.firstName,
      lastName: previousValues.lastName || userInitialValues.lastName,
    }));
  }, [userInitialValues]);

  const focusPreviousElement = useCallback(() => {
    const previousElement = previousFocusRef.current;
    if (previousElement && typeof previousElement.focus === 'function') {
      window.requestAnimationFrame(() => previousElement.focus());
    }
  }, []);

  const closePopup = useCallback(
    ({ persistDismissal = true } = {}) => {
      if (persistDismissal) {
        storageSet(MAILCHIMP_POPUP_DISMISSED_AT_KEY, String(Date.now()));
      }

      setIsOpen(false);
      setStatus('idle');
      setErrorMessage('');
      focusPreviousElement();
    },
    [focusPreviousElement]
  );

  useEffect(() => {
    if (!popupEnabled) {
      return undefined;
    }

    if (isOpen && isMailchimpPopupRouteExcluded(location.pathname)) {
      setIsOpen(false);
      return undefined;
    }

    if (isOpen || shouldSuppressPopup({ pathname: location.pathname, dismissDays })) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      previousFocusRef.current = document.activeElement;
      setIsOpen(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [delayMs, dismissDays, isOpen, location.pathname, popupEnabled]);

  useEffect(() => {
    return () => {
      if (successCloseTimerRef.current) {
        window.clearTimeout(successCloseTimerRef.current);
      }
    };
  }, []);

  if (!popupEnabled || !isBrowser()) {
    return null;
  }

  const onChange = event => {
    const { name, value } = event.target;
    setValues(previousValues => ({
      ...previousValues,
      [name]: value,
    }));
  };

  const onSubmit = async event => {
    event.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await subscribeToMailchimp({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
      });
      const responseData = response?.data || {};

      if (responseData.success === false) {
        throw new Error(responseData.message);
      }

      storageSet(MAILCHIMP_POPUP_SUBSCRIBED_KEY, 'true');
      setStatus('success');
      successCloseTimerRef.current = window.setTimeout(() => {
        closePopup({ persistDismissal: false });
      }, SUCCESS_CLOSE_DELAY_MS);
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        'Something went wrong. Please try again.';
      setErrorMessage(message);
      setStatus('error');
    }
  };

  const isSubmitting = status === 'submitting';
  const isSuccess = status === 'success';
  const titleId = 'mailchimp-subscriber-popup-title';
  const descriptionId = 'mailchimp-subscriber-popup-description';

  return (
    <Modal
      id="MailchimpSubscriberPopup"
      isOpen={isOpen}
      onClose={() => closePopup({ persistDismissal: true })}
      onManageDisableScrolling={(componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling))
      }
      usePortal
      modalRole="dialog"
      ariaLabelledBy={titleId}
      ariaDescribedBy={descriptionId}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
    >
      <div className={css.header}>
        <h2 id={titleId} className={css.title}>
          Join our mailing list
        </h2>
        <p id={descriptionId} className={css.description}>
          Get updates, new listings, and exclusive marketplace news directly in your inbox.
        </p>
      </div>

      {isSuccess ? (
        <div className={css.success} role="status" aria-live="polite">
          Thanks for subscribing.
        </div>
      ) : (
        <form className={css.form} onSubmit={onSubmit}>
          <div className={css.field}>
            <label className={css.label} htmlFor="mailchimp-popup-email">
              Email address
            </label>
            <input
              id="mailchimp-popup-email"
              className={css.input}
              type="email"
              name="email"
              autoComplete="email"
              required
              value={values.email}
              onChange={onChange}
              disabled={isSubmitting}
            />
          </div>

          <div className={css.nameFields}>
            <div className={css.field}>
              <label className={css.label} htmlFor="mailchimp-popup-first-name">
                First name
              </label>
              <input
                id="mailchimp-popup-first-name"
                className={css.input}
                type="text"
                name="firstName"
                autoComplete="given-name"
                value={values.firstName}
                onChange={onChange}
                disabled={isSubmitting}
              />
            </div>
            <div className={css.field}>
              <label className={css.label} htmlFor="mailchimp-popup-last-name">
                Last name
              </label>
              <input
                id="mailchimp-popup-last-name"
                className={css.input}
                type="text"
                name="lastName"
                autoComplete="family-name"
                value={values.lastName}
                onChange={onChange}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {status === 'error' ? (
            <div className={css.error} role="alert">
              {errorMessage || 'Something went wrong. Please try again.'}
            </div>
          ) : null}

          <div className={css.actions}>
            <PrimaryButton type="submit" inProgress={isSubmitting} disabled={isSubmitting}>
              Subscribe
            </PrimaryButton>
            <SecondaryButton
              type="button"
              className={css.secondaryAction}
              onClick={() => closePopup({ persistDismissal: true })}
              disabled={isSubmitting}
            >
              Maybe later
            </SecondaryButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default MailchimpSubscriberPopup;
